import React, {useState, useEffect} from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    Button,
    Box,
    Divider,
    DialogTitle,
} from '@mui/material';
import dayjs, {Dayjs} from 'dayjs';
import {
    FileDataDialog,
    FileEntity,
    FilesCategoryType,
    FileTag,
    ReportType,
    DocumentType,
} from '@pages/FilesPage';
import {FileUploadTagsSidebar} from './FileUploadTagsSidebar';
import {UploadFileList} from './UploadFileList';
import {DT_FORMATS} from 'utils';
import {wordsDeclension} from 'utils/functions/typography';
import {FileType} from '@features/files/types';
import {EmployeeFileUploadTagsSidebar} from '@features/employeeContent/employeeInfo/components/EmployeeFiles/EmployeeFileUploadTagsSidebar';
import {useTranslation} from 'react-i18next';

type FileUploadDialogProps = {
    open: boolean;
    onClose: () => void;
    onSave: (filesData: FileDataDialog[]) => void;
    onUpdate?: (fileId: string, documentDate: string, tagIds: string[], type: DocumentType) => void;
    files?: File[];
    category?: FilesCategoryType | null;
    allTags?: FileTag[];
    isEditing?: boolean;
    fileToEdit?: FileEntity | null;
    refetch?: () => void;
    filesType?: FileType;
    employeeDialog?: boolean;
    showAddTagButton?: boolean;
};

export function FileUploadDialog({
    open,
    onClose,
    onSave,
    onUpdate,
    files,
    category,
    allTags,
    isEditing = false,
    fileToEdit,
    refetch,
    filesType,
    employeeDialog = false,
    showAddTagButton = true,
}: FileUploadDialogProps) {
    const {t} = useTranslation('common');
    const [filesData, setFilesData] = useState<FileDataDialog[]>([]);
    const [isTagSidebarOpen, setTagSidebarOpen] = useState(false);
    const [selectedFileIndex, setSelectedFileIndex] = useState<number | null>(null);

    const handleOpenTagSidebar = (fileIndex: number) => {
        setSelectedFileIndex(fileIndex);
        setTagSidebarOpen(true);
    };

    // Обновляем список тегов для выбранного файла
    const handleSelectTags = (tags: FileTag[]) => {
        if (selectedFileIndex === null) return;
        const updated = [...filesData];
        updated[selectedFileIndex].tags = tags;
        setFilesData(updated);
    };

    const handleFilenameChange =
        (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
            const updated = [...filesData];
            updated[index].filename = event.target.value;
            setFilesData(updated);
        };

    const handleDocumentDateChange = (index: number) => (newValue: Dayjs | null) => {
        const updated = [...filesData];
        updated[index].documentDate = newValue;
        setFilesData(updated);
    };

    const handleDeleteFile = (index: number) => () => {
        setFilesData((prev) => prev.filter((_, i) => i !== index));
    };

    //удаление тега из файла
    const handleDeleteTag = (fileIndex: number, tagId: string) => {
        const updated = [...filesData];
        updated[fileIndex].tags = updated[fileIndex].tags.filter((tag) => tag.id !== tagId);
        setFilesData(updated);

        if (selectedFileIndex === fileIndex) {
            handleSelectTags(updated[fileIndex].tags);
        }
    };

    const handleTypeChange = (index: number) => (newValue: ReportType | null) => {
        const updated = [...filesData];

        if ((category && !category.hasPlanFact) || newValue === null) {
            updated[index].type = null;
        } else {
            updated[index].type = newValue === ReportType.PLAN ? FileType.PLAN : FileType.FACT;
        }

        setFilesData(updated);
    };

    const handleSave = async () => {
        if (isEditing && fileToEdit && onUpdate) {
            const updatedFile = filesData[0];
            await onUpdate(
                fileToEdit.id,
                updatedFile.documentDate?.format(DT_FORMATS.utcDate) || '',
                updatedFile.tags.map((tag) => tag.id),
                updatedFile.type,
            );
            refetch?.();
        } else {
            onSave(filesData);
        }
        onClose();
    };

    const fileCount = filesData.length;
    const fileWord = wordsDeclension(fileCount, [
        `${t('file').toLowerCase()}`,
        `${t('genitiveFileSingular').toLowerCase()}`,
        `${t('genitiveFilePlural').toLowerCase()}`,
    ]);
    const createTitle =
        (fileCount === 1 ? `${t('uploaded1')} ` : `${t('uploaded2')} `) +
        fileCount +
        ' ' +
        fileWord;
    const dialogTitle = isEditing ? `${t('fileEditing')}` : createTitle;

    useEffect(() => {
        if (!open) {
            setFilesData([]);
            setTagSidebarOpen(false);
            setSelectedFileIndex(null);
            return;
        }

        if (isEditing && fileToEdit) {
            setFilesData([
                {
                    file: new File([], fileToEdit.originalFilename),
                    filename: fileToEdit.originalFilename,
                    documentDate: dayjs(fileToEdit.documentDate),
                    tags: fileToEdit.tags || [],
                    type: fileToEdit.type || null,
                },
            ]);
        } else if (!isEditing && files) {
            setFilesData(
                files.map((file) => ({
                    file,
                    filename: file.name,
                    documentDate: dayjs(),
                    tags: [],
                    type: null,
                })),
            );
        }
    }, [open, isEditing, fileToEdit, files]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={isTagSidebarOpen ? 'lg' : 'md'}
            PaperProps={{
                sx: {
                    height: fileCount <= 1 ? 400 : 620,
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogContent
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    p: 2,
                    overflow: 'hidden',
                }}
            >
                <Box
                    component='div'
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'row',
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        component='div'
                        sx={{flex: 1, overflowY: 'auto', pr: 2}}
                    >
                        <UploadFileList
                            filesData={filesData}
                            category={category}
                            allTags={allTags}
                            handleFilenameChange={handleFilenameChange}
                            handleDocumentDateChange={handleDocumentDateChange}
                            handleDeleteFile={handleDeleteFile}
                            handleOpenTagSidebar={handleOpenTagSidebar}
                            handleDeleteTag={handleDeleteTag}
                            isEditing={isEditing}
                            handleTypeChange={handleTypeChange}
                            filesType={filesType}
                            showAddTagButton={showAddTagButton}
                        />
                    </Box>
                    {isTagSidebarOpen && (
                        <>
                            <Divider
                                orientation='vertical'
                                flexItem
                            />
                            <Box
                                component='div'
                                sx={{pl: 2, overflowY: 'auto', width: '320px'}}
                            >
                                {employeeDialog ? (
                                    <EmployeeFileUploadTagsSidebar
                                        isOpen={isTagSidebarOpen}
                                        onClose={() => setTagSidebarOpen(false)}
                                        onSelectTags={handleSelectTags}
                                        selectedTags={filesData[selectedFileIndex!]?.tags || []}
                                        refetch={refetch}
                                    />
                                ) : (
                                    <FileUploadTagsSidebar
                                        isOpen={isTagSidebarOpen}
                                        onClose={() => setTagSidebarOpen(false)}
                                        onSelectTags={handleSelectTags}
                                        selectedTags={filesData[selectedFileIndex!]?.tags || []}
                                        refetch={refetch}
                                    />
                                )}
                            </Box>
                        </>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{padding: '0 24px 24px 0'}}>
                <Button
                    variant='outlined'
                    onClick={onClose}
                >
                    {t('cancel')}
                </Button>
                <Button
                    onClick={handleSave}
                    variant='contained'
                    color='primary'
                    disabled={filesData.length === 0}
                >
                    {t('save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
